import { getNavbarNavForRole } from 'core/constants/nav-navbar-by-role';
import { ADMIN_ROUTE_PATHS } from 'core/constants/admin-route-paths';
import { NavItem } from 'core/models/nav-item.model';
import { GroupNavMode } from 'core/models/platform-settings.model';
import { Role } from 'core/models/role.model';

export interface PlatformSearchEntry {
  readonly id: string;
  readonly label: string;
  readonly route: string;
  readonly iconClass: string;
  readonly groupLabel?: string;
  readonly queryParams?: Readonly<Record<string, string>>;
}

function entryKey(
  route: string,
  queryParams?: Readonly<Record<string, string>>,
): string {
  if (!queryParams || Object.keys(queryParams).length === 0) {
    return route;
  }

  const params = Object.entries(queryParams)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}=${value}`)
    .join('&');

  return `${route}?${params}`;
}

function normalizeQuery(value: string): string {
  return value.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().trim();
}

function pushEntry(
  map: Map<string, PlatformSearchEntry>,
  item: NavItem,
  groupLabel?: string,
): void {
  if (item.comingSoon || !item.route) {
    return;
  }

  const id = entryKey(item.route, item.queryParams);
  if (map.has(id)) {
    return;
  }

  map.set(id, {
    id,
    label: item.label,
    route: item.route,
    iconClass: item.iconClass,
    groupLabel,
    queryParams: item.queryParams,
  });
}

function collectEntries(
  items: readonly NavItem[],
  map: Map<string, PlatformSearchEntry>,
  groupLabel?: string,
): void {
  for (const item of items) {
    if (item.children && item.children.length > 0) {
      if (item.route) {
        pushEntry(map, item);
      }
      collectEntries(item.children, map, item.label);
      continue;
    }

    pushEntry(map, item, groupLabel);
  }
}

/** Indexa rotas do menu (incl. hubs Cadastrar/Listar/Vincular/Lixeira) para a busca global. */
export function buildPlatformSearchIndex(
  role: Role | null | undefined,
  groupNavMode: GroupNavMode = 'submenu',
): readonly PlatformSearchEntry[] {
  const map = new Map<string, PlatformSearchEntry>();
  collectEntries(getNavbarNavForRole(role, groupNavMode), map);

  pushEntry(map, {
    label: 'Notificações',
    route: ADMIN_ROUTE_PATHS.notificationsList,
    iconClass: 'bell',
  });

  return [...map.values()].sort((a, b) =>
    a.label.localeCompare(b.label, 'pt-BR'),
  );
}

export function filterPlatformSearchEntries(
  entries: readonly PlatformSearchEntry[],
  query: string,
): readonly PlatformSearchEntry[] {
  const normalized = normalizeQuery(query);
  if (!normalized) {
    return entries;
  }

  return entries.filter((entry) => {
    const haystack = normalizeQuery(
      [entry.label, entry.groupLabel, entry.route].filter(Boolean).join(' '),
    );
    return haystack.includes(normalized);
  });
}
